import { useState } from "react";
import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

interface Props {
  form: Invitation;
}

type Device = "desktop" | "tablet" | "mobile";

const DEVICE_CONFIG: Record<Device, { width: string; icon: string; label: string }> = {
  desktop: { width: "w-full",    icon: "ri-computer-line",  label: "Desktop" },
  tablet:  { width: "w-[768px]", icon: "ri-tablet-line",    label: "Tablet" },
  mobile:  { width: "w-[390px]", icon: "ri-smartphone-line", label: "Mobile" },
};

export default function PreviewTab({ form }: Props) {
  const [device, setDevice] = useState<Device>("desktop");
  const [key, setKey]       = useState(0);

  const publicUrl = form.public_slug ? `/invite/${form.public_slug}` : null;
  const cfg = DEVICE_CONFIG[device];

  if (!publicUrl) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100 text-slate-300">
          <i className="ri-eye-off-line text-3xl"></i>
        </div>
        <div className="text-center">
          <p className="text-slate-600 font-semibold text-sm">No public slug set</p>
          <p className="text-slate-400 text-xs mt-1">Go to the Publish tab and set a URL slug to preview the invitation.</p>
        </div>
      </div>
    );
  }

  if (!form.is_published) {
    return (
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500 shrink-0 mt-0.5">
            <i className="ri-alert-line"></i>
          </span>
          <div>
            <p className="text-amber-700 font-medium text-sm">Invitation is not published</p>
            <p className="text-amber-600 text-xs mt-0.5">
              The preview below shows how the invitation will look. Publish it from the Publish tab to make it accessible to guests.
            </p>
          </div>
        </div>

        <PreviewFrame publicUrl={publicUrl} device={device} cfg={cfg} setDevice={setDevice} reloadKey={key} onReload={() => setKey((k) => k + 1)} />
      </div>
    );
  }

  return (
    <PreviewFrame publicUrl={publicUrl} device={device} cfg={cfg} setDevice={setDevice} reloadKey={key} onReload={() => setKey((k) => k + 1)} />
  );
}

/* ─── Inner frame component ──────────────────────────────────── */
function PreviewFrame({
  publicUrl,
  device,
  cfg,
  setDevice,
  reloadKey,
  onReload,
}: {
  publicUrl: string;
  device: Device;
  cfg: { width: string; icon: string; label: string };
  setDevice: (d: Device) => void;
  reloadKey: number;
  onReload: () => void;
}) {
  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Device switcher */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {(["desktop", "tablet", "mobile"] as Device[]).map((d) => {
            const c = DEVICE_CONFIG[d];
            return (
              <button
                key={d}
                onClick={() => setDevice(d)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  device === d ? "bg-white text-slate-700 shadow-sm" : "text-slate-400 hover:text-slate-600"
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={c.icon}></i>
                </span>
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onReload}
            className="flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-slate-700 text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-refresh-line"></i>
            </span>
            Reload
          </button>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-external-link-line"></i>
            </span>
            Open in new tab
          </a>
        </div>
      </div>

      {/* URL bar */}
      <div className="flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2">
        <span className="w-3.5 h-3.5 flex items-center justify-center text-slate-400">
          <i className="ri-lock-line text-xs"></i>
        </span>
        <p className="text-slate-500 text-xs font-mono flex-1 truncate">
          {window.location.origin}{publicUrl}
        </p>
      </div>

      {/* iframe container */}
      <div className="bg-slate-200 rounded-xl p-3 overflow-auto">
        <div className={`mx-auto transition-all ${cfg.width} min-w-0`}>
          <div className="bg-white rounded-lg overflow-hidden border border-slate-300" style={{ height: "600px" }}>
            <iframe
              key={reloadKey}
              src={publicUrl}
              title="Invitation Preview"
              className="w-full h-full border-0"
              style={{ minWidth: device === "desktop" ? "1024px" : undefined }}
            />
          </div>
        </div>
      </div>

      <p className="text-slate-400 text-xs text-center">
        Preview may differ slightly from the live page. Save changes and reload to see updates.
      </p>
    </div>
  );
}
